import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  LinkOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH } from '@lens/shared';
import type { ApiToken } from '@lens/shared';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import { McpGuide } from '../mcp/McpGuide.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { TelegramProfileCard } from '../telegram/TelegramProfileCard.tsx';
import {
  createApiToken,
  setJiraToken,
  deleteJiraToken,
  listApiTokens,
  pingJira,
  revokeApiToken,
  setLlmApiKey,
  deleteLlmApiKey,
  pingLlm,
  setAvatar,
  deleteAvatar,
  updateProfile,
  changePassword,
} from './api.ts';
import { AvatarCropModal } from './AvatarCropModal.tsx';
import {
  resetAllPreferences,
  getTableFontSize,
  setTableFontSize,
  TABLE_FONT_SIZE_OPTIONS,
  DEFAULT_TABLE_FONT_SIZE,
} from '../../shared/prefs/store.ts';
import styles from './ProfilePage.module.css';
import { ProfileSection, SettingRow, StatusChip } from './ProfileSection.tsx';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

const { Text } = Typography;

const fmtDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * Профиль пользователя: оглавление слева и разделы справа — кто я,
 * безопасность, Telegram, личные ключи (Jira, модель), токены API и MCP,
 * настройки интерфейса. Каждая настройка — строка «что это / состояние и
 * действие»; поля ввода раскрываются по кнопке.
 */
export function ProfilePage(): JSX.Element {
  useDocumentTitle('Профиль');
  const { user, patchUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarDeleting, setAvatarDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [token, setToken] = useState('');
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pingResult, setPingResult] = useState<{
    ok: boolean;
    userName?: string;
    error?: string;
  } | null>(null);

  // LLM key state
  const [llmKey, setLlmKey] = useState('');
  const [llmSaving, setLlmSaving] = useState(false);
  const [llmDeleting, setLlmDeleting] = useState(false);
  const [llmPinging, setLlmPinging] = useState(false);
  const [hasLlmKey, setHasLlmKey] = useState(!!user?.hasLlmApiKey);
  const [llmPingResult, setLlmPingResult] = useState<{
    ok: boolean;
    model?: string;
    error?: string;
  } | null>(null);

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [resettingPrefs, setResettingPrefs] = useState(false);
  // Поля ввода ключей и форма пароля свёрнуты, пока их не открыли: так
  // страница читается как список «что подключено», а не как стена форм.
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [jiraOpen, setJiraOpen] = useState(false);
  const [llmOpen, setLlmOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(() => window.location.hash === '#mcp');
  const [fontSize, setFontSizeState] = useState<number>(getTableFontSize());
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    setTokensLoading(true);
    try {
      const res = await listApiTokens();
      setTokens(res.tokens);
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось загрузить токены');
    } finally {
      setTokensLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  // Переход из меню под аватаром и ⌘K ведёт на /profile#mcp — доводим до блока.
  const { hash } = useLocation();

  // Клик по пункту меню, когда мы уже в профиле, — только меняет хэш.
  useEffect(() => {
    if (hash !== '#mcp') return;
    setMcpOpen(true);
    document.getElementById('mcp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, tokensLoading]);

  const handleCreateToken = async (values: { name: string; expiresInDays?: number | null }) => {
    setCreating(true);
    try {
      const res = await createApiToken({
        name: values.name,
        expiresInDays: values.expiresInDays ?? null,
      });
      setNewSecret(res.secret);
      setCreateOpen(false);
      form.resetFields();
      await loadTokens();
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось создать токен');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeToken = async (id: string) => {
    setRevokingId(id);
    try {
      await revokeApiToken(id);
      void message.success('Токен отозван');
      await loadTokens();
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось отозвать токен');
    } finally {
      setRevokingId(null);
    }
  };

  if (!user) return <div />;

  const handleCheck = async () => {
    if (!token.trim()) {
      void message.warning('Введите токен');
      return;
    }
    setChecking(true);
    setPingResult(null);
    try {
      await setJiraToken(token);
      const result = await pingJira();
      if (result.ok) {
        setPingResult({ ok: true, userName: `${result.user.displayName} (${result.user.name})` });
        void message.success('Токен проверен и сохранён');
      } else {
        await deleteJiraToken().catch(() => {});
        setPingResult({ ok: false, error: result.error ?? 'Неверный токен' });
      }
    } catch (err: any) {
      await deleteJiraToken().catch(() => {});
      if (err.code === 'jira_not_configured') {
        setPingResult({ ok: false, error: 'Jira-интеграция не настроена администратором' });
      } else if (err.code === 'jira_unauthorized') {
        setPingResult({ ok: false, error: 'Неверный токен' });
      } else {
        setPingResult({ ok: false, error: err.message ?? 'Ошибка проверки' });
      }
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteJiraToken();
      setToken('');
      setPingResult(null);
      void message.success('Токен удалён');
    } catch (err: any) {
      void message.error(err.message ?? 'Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword) {
      void message.warning('Введите текущий пароль');
      return;
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      void message.warning(
        `Новый пароль должен содержать не менее ${PASSWORD_MIN_LENGTH} символов`,
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      void message.warning('Пароли не совпадают');
      return;
    }
    if (newPassword === oldPassword) {
      void message.warning('Новый пароль совпадает с текущим');
      return;
    }
    setPasswordSaving(true);
    try {
      await changePassword({ oldPassword, newPassword });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      void message.success('Пароль изменён. Другие сессии завершены.');
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось изменить пароль');
    } finally {
      setPasswordSaving(false);
    }
  };

  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      void message.warning('ФИО не может быть пустым');
      return;
    }
    if (trimmed === user.displayName) {
      setEditingName(false);
      return;
    }
    setNameSaving(true);
    try {
      const res = await updateProfile({ displayName: trimmed });
      patchUser({ displayName: res.user.displayName });
      setEditingName(false);
      void message.success('ФИО обновлено');
    } catch (err: any) {
      void message.error(err?.message ?? 'Ошибка сохранения');
    } finally {
      setNameSaving(false);
    }
  };

  const isSso = user.authMethod === 'sso';

  return (
    <div className={styles.shell}>
      <div className={styles.page}>
        <ProfileNav />

        <div className={styles.content}>
          {/* ─── Шапка: кто я ─────────────────────────────────────────── */}
          <div className={styles.hero} id="account" data-profile-section="account">
            <Tooltip title="Сменить фото">
              <div
                className={styles.avatarWrap}
                role="button"
                tabIndex={0}
                aria-label="Сменить фото"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                }}
              >
                <Avatar
                  size={72}
                  src={user.avatarUrl ?? undefined}
                  icon={!user.avatarUrl ? <UserOutlined /> : undefined}
                />
                <span className={styles.avatarOverlay}>
                  {avatarSaving ? '…' : <CameraOutlined style={{ fontSize: 18 }} />}
                </span>
              </div>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setCropFile(file);
              }}
            />

            <div className={styles.heroText}>
              {editingName ? (
                <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
                  <Input
                    value={nameDraft}
                    autoFocus
                    maxLength={120}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onPressEnter={saveName}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    disabled={nameSaving}
                    aria-label="ФИО"
                  />
                  <Button type="primary" loading={nameSaving} onClick={saveName}>
                    Сохранить
                  </Button>
                  <Button onClick={() => setEditingName(false)} disabled={nameSaving}>
                    Отмена
                  </Button>
                </Space.Compact>
              ) : (
                <div className={styles.heroName}>
                  <span>{user.displayName}</span>
                  <Tooltip title="Изменить ФИО">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setNameDraft(user.displayName);
                        setEditingName(true);
                      }}
                      aria-label="Изменить ФИО"
                    />
                  </Tooltip>
                </div>
              )}
              <div className={styles.heroMeta}>
                <span>{user.login}</span>
                {user.email && <span>{user.email}</span>}
                <StatusChip on={false}>{isSso ? 'Вход через SSO' : 'Вход по паролю'}</StatusChip>
                {user.isPlatformAdmin && <StatusChip on>Администратор платформы</StatusChip>}
              </div>
              {user.avatarUrl && (
                <div className={styles.heroActions}>
                  <Button
                    type="link"
                    size="small"
                    danger
                    style={{ padding: 0, height: 'auto' }}
                    loading={avatarDeleting}
                    onClick={async () => {
                      setAvatarDeleting(true);
                      try {
                        await deleteAvatar();
                        patchUser({ avatarUrl: null });
                        void message.success('Фото удалено');
                      } catch (err: any) {
                        void message.error(err?.message ?? 'Ошибка');
                      } finally {
                        setAvatarDeleting(false);
                      }
                    }}
                  >
                    Удалить фото
                  </Button>
                </div>
              )}
            </div>
          </div>

          <AvatarCropModal
            open={!!cropFile}
            file={cropFile}
            saving={avatarSaving}
            onCancel={() => setCropFile(null)}
            onConfirm={async (dataUrl) => {
              setAvatarSaving(true);
              try {
                const res = await setAvatar(dataUrl);
                patchUser({ avatarUrl: res.avatarUrl });
                setCropFile(null);
                void message.success('Фото обновлено');
              } catch (err: any) {
                void message.error(err?.message ?? 'Ошибка загрузки');
              } finally {
                setAvatarSaving(false);
              }
            }}
          />

          {/* ─── Безопасность ─────────────────────────────────────────── */}
          <ProfileSection id="security" title="Безопасность">
            {isSso ? (
              <SettingRow
                title="Пароль"
                hint="Вы входите через AlfaProject — пароль меняется там, а не в Lens."
                aside={<StatusChip on>Вход через SSO</StatusChip>}
              />
            ) : (
              <SettingRow
                title="Пароль"
                hint={`Не короче ${PASSWORD_MIN_LENGTH} символов. После смены остальные ваши сессии завершатся, текущая останется.`}
                aside={
                  !passwordOpen && (
                    <Button onClick={() => setPasswordOpen(true)} data-testid="password-open">
                      Сменить пароль
                    </Button>
                  )
                }
              >
                {passwordOpen && (
                  <div style={{ display: 'grid', gap: 10, maxWidth: 360 }}>
                    <Input.Password
                      placeholder="Текущий пароль"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      autoComplete="current-password"
                      autoFocus
                      data-testid="password-old-input"
                    />
                    <Input.Password
                      placeholder="Новый пароль"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      data-testid="password-new-input"
                    />
                    <Input.Password
                      placeholder="Повторите новый пароль"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onPressEnter={handleChangePassword}
                      autoComplete="new-password"
                      data-testid="password-confirm-input"
                    />
                    <Space>
                      <Button
                        type="primary"
                        loading={passwordSaving}
                        onClick={handleChangePassword}
                        data-testid="password-change-submit"
                      >
                        Изменить пароль
                      </Button>
                      <Button
                        onClick={() => {
                          setPasswordOpen(false);
                          setOldPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        disabled={passwordSaving}
                      >
                        Отмена
                      </Button>
                    </Space>
                  </div>
                )}
              </SettingRow>
            )}
          </ProfileSection>

          {/* ─── Уведомления в Telegram ───────────────────────────────── */}
          <ProfileSection
            id="telegram"
            title="Telegram"
            description="Бот Lens: записи из рабочих чатов с вами в авторах и уведомления об упоминаниях и назначениях."
          >
            <TelegramProfileCard />
          </ProfileSection>

          {/* ─── Интеграции ───────────────────────────────────────────── */}
          <ProfileSection
            id="integrations"
            title="Интеграции"
            description="Личные ключи: Lens ходит в Jira и к языковой модели от вашего имени."
          >
            <SettingRow
              title="Jira"
              hint={
                <>
                  Личный токен — синхронизация записей с Jira идёт от вашего имени.{' '}
                  <a
                    href="https://jira.moscow.alfaintra.net/secure/ViewProfile.jspa"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Где взять токен
                  </a>
                </>
              }
              aside={
                <>
                  <StatusChip on={user.hasJiraToken}>
                    {user.hasJiraToken ? 'Токен задан' : 'Не задан'}
                  </StatusChip>
                  {!jiraOpen && (
                    <Button onClick={() => setJiraOpen(true)} data-testid="jira-open">
                      {user.hasJiraToken ? 'Заменить' : 'Добавить'}
                    </Button>
                  )}
                </>
              }
            >
              {jiraOpen && (
                <>
                  <Space.Compact style={{ width: '100%', maxWidth: 480 }}>
                    <Input.Password
                      placeholder="Персональный токен Jira"
                      value={token}
                      autoFocus
                      onChange={(e) => {
                        setToken(e.target.value);
                        setPingResult(null);
                      }}
                      onPressEnter={handleCheck}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      name="lens-jira-token"
                      data-testid="jira-token-input"
                    />
                    <Button
                      type="primary"
                      onClick={handleCheck}
                      loading={checking}
                      data-testid="jira-token-check"
                    >
                      Проверить и сохранить
                    </Button>
                  </Space.Compact>
                  {pingResult && (
                    <PingResult {...pingResult} okText={`Подключено как ${pingResult.userName}`} />
                  )}
                  <Space style={{ marginTop: 12 }}>
                    <Button onClick={() => setJiraOpen(false)}>Готово</Button>
                    {user.hasJiraToken && (
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={handleDelete}
                        loading={deleting}
                      >
                        Удалить токен
                      </Button>
                    )}
                  </Space>
                </>
              )}
            </SettingRow>

            <SettingRow
              title={
                <Space size={6}>
                  <AiSparkIcon /> Ключ языковой модели
                </Space>
              }
              hint="Для ИИ-помощника. Пока ключа нет, помощник недоступен."
              aside={
                <>
                  <StatusChip on={hasLlmKey}>{hasLlmKey ? 'Ключ задан' : 'Не задан'}</StatusChip>
                  {!llmOpen && (
                    <Button onClick={() => setLlmOpen(true)} data-testid="llm-open">
                      {hasLlmKey ? 'Изменить' : 'Добавить'}
                    </Button>
                  )}
                </>
              }
            >
              {llmOpen && (
                <>
                  <Space.Compact style={{ width: '100%', maxWidth: 480 }}>
                    <Input.Password
                      placeholder="sk-..."
                      value={llmKey}
                      autoFocus
                      onChange={(e) => setLlmKey(e.target.value)}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      name="lens-llm-key"
                    />
                    <Button
                      type="primary"
                      loading={llmSaving}
                      onClick={async () => {
                        if (!llmKey.trim()) {
                          void message.warning('Введите ключ');
                          return;
                        }
                        setLlmSaving(true);
                        try {
                          await setLlmApiKey(llmKey.trim());
                          setHasLlmKey(true);
                          setLlmKey('');
                          void message.success('Ключ сохранён');
                        } catch (err: any) {
                          void message.error(err.message ?? 'Ошибка сохранения');
                        } finally {
                          setLlmSaving(false);
                        }
                      }}
                    >
                      Сохранить
                    </Button>
                  </Space.Compact>
                  {llmPingResult && (
                    <PingResult
                      {...llmPingResult}
                      okText={`Подключено. Модель: ${llmPingResult.model}`}
                    />
                  )}
                  <Space wrap style={{ marginTop: 12 }}>
                    <Button onClick={() => setLlmOpen(false)}>Готово</Button>
                    {hasLlmKey && (
                      <Button
                        loading={llmPinging}
                        onClick={async () => {
                          setLlmPinging(true);
                          setLlmPingResult(null);
                          try {
                            const res = await pingLlm();
                            setLlmPingResult({ ok: true, model: res.model });
                          } catch (err: any) {
                            setLlmPingResult({
                              ok: false,
                              error: err.message ?? 'Ошибка подключения',
                            });
                          } finally {
                            setLlmPinging(false);
                          }
                        }}
                      >
                        Проверить подключение
                      </Button>
                    )}
                    {hasLlmKey && (
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        loading={llmDeleting}
                        onClick={async () => {
                          setLlmDeleting(true);
                          try {
                            await deleteLlmApiKey();
                            setHasLlmKey(false);
                            setLlmKey('');
                            setLlmPingResult(null);
                            void message.success('Ключ удалён');
                          } catch (err: any) {
                            void message.error(err.message ?? 'Ошибка');
                          } finally {
                            setLlmDeleting(false);
                          }
                        }}
                      >
                        Удалить ключ
                      </Button>
                    )}
                  </Space>
                </>
              )}
            </SettingRow>
          </ProfileSection>

          {/* ─── API и MCP ────────────────────────────────────────────── */}
          <ProfileSection
            id="api"
            title="API и MCP"
            description="Личные токены для скриптов, CI и подключения Lens к ассистентам по MCP. Секрет показывается один раз."
          >
            <SettingRow
              title="Токены доступа"
              hint={
                tokensLoading
                  ? 'Загружаем…'
                  : tokens.length === 0
                    ? 'Токенов пока нет.'
                    : `Действующих токенов: ${tokens.length}.`
              }
              aside={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                  Создать токен
                </Button>
              }
            >
              {tokens.length > 0 && (
                <Table
                  dataSource={tokens}
                  loading={tokensLoading}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Название', dataIndex: 'name' },
                    {
                      title: 'Префикс',
                      dataIndex: 'prefix',
                      render: (p: string) => <Text code>{p}</Text>,
                    },
                    {
                      title: 'Использовался',
                      dataIndex: 'lastUsedAt',
                      render: (v: string | null) => fmtDate(v),
                    },
                    {
                      title: 'Истекает',
                      dataIndex: 'expiresAt',
                      render: (v: string | null) => (v ? fmtDate(v) : 'бессрочно'),
                    },
                    {
                      title: '',
                      key: 'actions',
                      align: 'right',
                      render: (_: unknown, rec: ApiToken) => (
                        <Popconfirm
                          title="Отозвать токен?"
                          description="Доступ по этому токену прекратится немедленно."
                          okText="Отозвать"
                          okButtonProps={{ danger: true }}
                          cancelText="Отмена"
                          onConfirm={() => {
                            void handleRevokeToken(rec.id);
                          }}
                        >
                          <Button danger type="text" size="small" loading={revokingId === rec.id}>
                            Отозвать
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              )}
            </SettingRow>

            <SettingRow
              id="mcp"
              title={
                <Space size={6}>
                  <ApiOutlined /> Как подключить MCP
                </Space>
              }
              hint="Пошаговая инструкция для Claude, Cursor и других ассистентов."
              aside={
                <Button onClick={() => setMcpOpen((v) => !v)} data-testid="mcp-guide-toggle">
                  {mcpOpen ? 'Свернуть' : 'Показать'}
                </Button>
              }
            >
              {mcpOpen && <McpGuide />}
            </SettingRow>
          </ProfileSection>

          {/* ─── Интерфейс ────────────────────────────────────────────── */}
          <ProfileSection id="interface" title="Интерфейс">
            <SettingRow
              title="Размер шрифта"
              hint="Во всём интерфейсе: таблицах, меню, формах. Меньше шрифт — больше помещается на экране."
              aside={
                <Select
                  value={fontSize}
                  style={{ width: 170 }}
                  onChange={(val) => {
                    setFontSizeState(val);
                    setTableFontSize(val);
                  }}
                  options={TABLE_FONT_SIZE_OPTIONS.map((sz) => ({
                    value: sz,
                    label: sz === DEFAULT_TABLE_FONT_SIZE ? `${sz} px (обычный)` : `${sz} px`,
                  }))}
                />
              }
            />
            <SettingRow
              title="Мои настройки представлений"
              hint="Фильтры, сортировки, поиск, ширина и порядок колонок, группировка. Сброс вернёт все представления к виду по умолчанию — только для вас."
              aside={
                <Popconfirm
                  title="Сбросить настройки представлений?"
                  description="Ваши фильтры, сортировки и настройки таблиц удалятся. Страница перезагрузится."
                  okText="Сбросить"
                  okButtonProps={{ danger: true }}
                  cancelText="Отмена"
                  onConfirm={async () => {
                    setResettingPrefs(true);
                    try {
                      await resetAllPreferences();
                      void message.success('Настройки сброшены');
                      // Перезагружаем, чтобы хуки переинициализировались с дефолтами.
                      window.location.reload();
                    } catch (err: any) {
                      void message.error(err?.message ?? 'Не удалось сбросить настройки');
                      setResettingPrefs(false);
                    }
                  }}
                >
                  <Button danger icon={<ReloadOutlined />} loading={resettingPrefs}>
                    Сбросить
                  </Button>
                </Popconfirm>
              }
            />
          </ProfileSection>
        </div>

        <Modal
          open={createOpen}
          title="Создать токен доступа"
          okText="Создать"
          cancelText="Отмена"
          confirmLoading={creating}
          onCancel={() => {
            setCreateOpen(false);
            form.resetFields();
          }}
          onOk={() => {
            form
              .validateFields()
              .then((values) => {
                void handleCreateToken(values);
              })
              .catch(() => {});
          }}
        >
          <Form form={form} layout="vertical" initialValues={{ expiresInDays: 90 }}>
            <Form.Item
              name="name"
              label="Название"
              rules={[{ required: true, message: 'Укажите название токена' }]}
            >
              <Input placeholder="Например: CI deploy" maxLength={120} />
            </Form.Item>
            <Form.Item name="expiresInDays" label="Срок жизни">
              <Select
                options={[
                  { value: 30, label: '30 дней' },
                  { value: 90, label: '90 дней' },
                  { value: 365, label: '365 дней' },
                  { value: null, label: 'Бессрочно' },
                ]}
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          open={newSecret !== null}
          title="Токен создан"
          footer={[
            <Button key="close" type="primary" onClick={() => setNewSecret(null)}>
              Готово
            </Button>,
          ]}
          onCancel={() => setNewSecret(null)}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Сохраните токен сейчас"
            description="Это единственный раз, когда показан полный секрет. Повторно его получить будет нельзя."
          />
          <Space.Compact style={{ width: '100%' }}>
            <Input readOnly value={newSecret ?? ''} style={{ fontFamily: 'monospace' }} />
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                if (newSecret) {
                  void navigator.clipboard
                    .writeText(newSecret)
                    .then(() => message.success('Скопировано'))
                    .catch(() => message.error('Не удалось скопировать'));
                }
              }}
            >
              Скопировать
            </Button>
          </Space.Compact>
        </Modal>
      </div>
    </div>
  );
}

/** Итог проверки подключения — строкой под полем ввода. */
function PingResult({
  ok,
  error,
  okText,
}: {
  ok: boolean;
  error?: string;
  okText: string;
}): JSX.Element {
  return (
    <div style={{ marginTop: 10 }}>
      {ok ? (
        <Text type="success">
          <CheckCircleOutlined /> {okText}
        </Text>
      ) : (
        <Text type="danger">
          <CloseCircleOutlined /> {error}
        </Text>
      )}
    </div>
  );
}

const NAV_ITEMS: Array<{ id: string; label: string; icon: JSX.Element }> = [
  { id: 'account', label: 'Профиль', icon: <UserOutlined /> },
  { id: 'security', label: 'Безопасность', icon: <LockOutlined /> },
  { id: 'telegram', label: 'Telegram', icon: <SendOutlined /> },
  { id: 'integrations', label: 'Интеграции', icon: <LinkOutlined /> },
  { id: 'api', label: 'API и MCP', icon: <KeyOutlined /> },
  { id: 'interface', label: 'Интерфейс', icon: <SettingOutlined /> },
];

/**
 * Оглавление: переход к разделу и подсветка того, что сейчас на экране.
 * На узком экране — полоса вкладок над содержимым.
 */
function ProfileNav(): JSX.Element {
  const [active, setActive] = useState('account');

  useEffect(() => {
    // Активен последний раздел, чей верх уже поднялся к верху экрана; у
    // самого низа страницы — последний раздел (он может быть короче экрана
    // и до верха не доехать). Страница прокручивается внутри оболочки,
    // поэтому слушаем прокрутку любого контейнера.
    const update = () => {
      const tops = NAV_ITEMS.map((i) => ({
        id: i.id,
        top: document.getElementById(i.id)?.getBoundingClientRect().top ?? Infinity,
      }));
      const last = document.getElementById(NAV_ITEMS[NAV_ITEMS.length - 1]!.id);
      const atBottom = last ? last.getBoundingClientRect().bottom <= window.innerHeight + 4 : false;
      if (atBottom) {
        setActive(NAV_ITEMS[NAV_ITEMS.length - 1]!.id);
        return;
      }
      let current = NAV_ITEMS[0]!.id;
      for (const t of tops) if (t.top <= 120) current = t.id;
      setActive(current);
    };
    update();
    document.addEventListener('scroll', update, { capture: true, passive: true });
    window.addEventListener('resize', update);
    return () => {
      document.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <nav className={styles.nav} aria-label="Разделы профиля">
      <h1 className={styles.navTitle}>Профиль</h1>
      {NAV_ITEMS.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          className={`${styles.navLink} ${active === item.id ? styles.navLinkActive : ''}`}
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById(item.id)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            window.history.replaceState(null, '', `#${item.id}`);
          }}
        >
          {item.icon}
          {item.label}
        </a>
      ))}
    </nav>
  );
}
